import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

interface FilterOption {
  value: string
}

interface Params<Option extends FilterOption> {
  options: Array<Option>
  selectedValue: string
  defaultValue: string
  enabled: boolean
  availabilityKey?: string
  onSelectedValueChange: (value: string) => void
}

function useLoyaltySpendPointsFilterAvailability<Option extends FilterOption>({
  options,
  selectedValue,
  defaultValue,
  enabled,
  availabilityKey,
  onSelectedValueChange,
}: Params<Option>) {
  const [availability, setAvailability] = useState<Record<string, boolean>>({})
  const previousAvailabilityKey = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (!enabled) {
      setAvailability({})
      previousAvailabilityKey.current = undefined
      return
    }

    if (availabilityKey !== undefined && previousAvailabilityKey.current !== undefined && previousAvailabilityKey.current !== availabilityKey) {
      setAvailability({})
    }
    previousAvailabilityKey.current = availabilityKey
  }, [availabilityKey, enabled])

  const onAvailabilityChange = useCallback((value: string, isAvailable: boolean) => {
    setAvailability(current => {
      return current[value] === isAvailable
        ? current
        : { ...current, [value]: isAvailable }
    })
  }, [])

  const regionalOptions = useMemo(
    () => options.filter(option => option.value !== defaultValue),
    [defaultValue, options],
  )
  const hasRegionalOptions = regionalOptions.length > 0

  const isResolved = enabled && availabilityKey !== undefined && regionalOptions
    .every(option => availability[option.value] !== undefined)

  useEffect(() => {
    if (enabled && isResolved && selectedValue !== defaultValue && !availability[selectedValue]) {
      onSelectedValueChange(defaultValue)
    }
  }, [availability, defaultValue, enabled, isResolved, onSelectedValueChange, selectedValue])

  const visibleOptions = useMemo(() => {
    if (!hasRegionalOptions) {
      return []
    }

    if (!enabled || !isResolved) {
      return options
    }

    const optionsWithAvailability = options.filter(
      option => option.value === defaultValue || availability[option.value],
    )

    return optionsWithAvailability.some(option => option.value !== defaultValue)
      ? optionsWithAvailability
      : []
  }, [availability, defaultValue, enabled, hasRegionalOptions, isResolved, options])

  const hasAvailableRegionalFilters = useMemo(
    () => visibleOptions.some(option => option.value !== defaultValue),
    [defaultValue, visibleOptions],
  )

  const shouldShowFilters = hasRegionalOptions && (
    !enabled || !isResolved || hasAvailableRegionalFilters
  )

  return { visibleOptions, onAvailabilityChange, isResolved, shouldShowFilters }
}

export default useLoyaltySpendPointsFilterAvailability
